import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { EuStars } from "./EuStars";
import { useLang, LangSwitcher } from "../lib/i18n";

const PRODUCT_ITEMS = [
    { en: "Research Assistant", tr: "Araştırma Asistanı", to: "/login?next=/app/research" },
    { en: "Case Law Explorer", tr: "İçtihat Gezgini", to: "/login?next=/app/case-law" },
    { en: "Compliance Checker", tr: "Uyum Kontrolü", to: "/login?next=/app/compliance" },
    { en: "Regulatory Intelligence", tr: "Mevzuat Takibi", to: "/login?next=/app/regulatory" },
    { en: "Watchdogs & Alerts", tr: "Takipçiler ve Uyarılar", to: "/login?next=/app/watchdogs" },
    { en: "Bulletins", tr: "Bültenler", to: "/bulletins" },
];

const LINKS = [
    { en: "Pricing", tr: "Fiyatlandırma", to: "/pricing", testid: "nav-link-pricing" },
    { en: "Bulletins", tr: "Bültenler", to: "/bulletins", testid: "nav-link-bulletins" },
];

export default function PublicNav() {
    const { T } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [productOpen, setProductOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const dropRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setProductOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [drawerOpen]);

    const go = (to) => {
        setDrawerOpen(false);
        setProductOpen(false);
        navigate(to);
    };

    return (
        <>
            <header
                data-testid="public-nav"
                className={`sticky top-0 z-50 transition-colors duration-300 ${
                    scrolled
                        ? "bg-[#0D1B3E]/95 backdrop-blur-md border-b border-[#C9A84C]/25"
                        : "bg-[#0D1B3E] border-b border-transparent"
                }`}
            >
                <div className="container-edit flex items-center justify-between h-16 lg:h-[72px] text-[#F5F0E8]">
                    <Link to="/" data-testid="landing-brand" className="flex items-center gap-3 min-h-[44px]">
                        <EuStars size={28} />
                        <span className="font-display text-xl tracking-tight">EuroLaw Hub</span>
                    </Link>

                    {/* Desktop */}
                    <nav className="hidden lg:flex items-center gap-7">
                        <div className="relative" ref={dropRef}>
                            <button
                                type="button"
                                data-testid="nav-product-trigger"
                                onClick={() => setProductOpen((v) => !v)}
                                className="flex items-center gap-1.5 text-sm tracking-wide hover:text-[#C9A84C] transition-colors min-h-[44px]"
                                aria-expanded={productOpen}
                            >
                                {T("Product", "Ürün")}
                                <ChevronDown size={14} className={`transition-transform duration-200 ${productOpen ? "rotate-180" : ""}`} />
                            </button>
                            {productOpen && (
                                <div
                                    data-testid="nav-product-dropdown"
                                    className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 bg-[#0D1B3E] border border-[#C9A84C]/30 shadow-2xl py-3"
                                >
                                    {PRODUCT_ITEMS.map((it) => (
                                        <button
                                            key={it.en}
                                            type="button"
                                            onClick={() => go(it.to)}
                                            className="block w-full text-left text-sm text-[#F5F0E8]/85 hover:text-[#C9A84C] hover:bg-[#F5F0E8]/5 transition-colors px-5 py-2.5"
                                        >
                                            {T(it.en, it.tr)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {LINKS.map((l) => (
                            <Link key={l.en} to={l.to} data-testid={l.testid}
                                className="text-sm tracking-wide hover:text-[#C9A84C] transition-colors min-h-[44px] inline-flex items-center">
                                {T(l.en, l.tr)}
                            </Link>
                        ))}
                        <Link to="/login" data-testid="landing-link-login"
                            className="text-sm tracking-wide hover:text-[#C9A84C] transition-colors min-h-[44px] inline-flex items-center">
                            {T("Sign in", "Giriş yap")}
                        </Link>
                        <LangSwitcher dark testId="nav-lang-switcher" />
                        <Link to="/signup" data-testid="landing-cta-signup" className="btn-primary !py-2.5 !px-5 text-sm">
                            {T("Start Free Trial", "Ücretsiz Deneme")} <ArrowRight size={15} />
                        </Link>
                    </nav>

                    {/* Mobile */}
                    <div className="flex lg:hidden items-center gap-2">
                        <Link to="/signup" data-testid="landing-cta-signup-mobile" className="btn-primary !py-2 !px-4 text-sm min-h-[44px]">
                            {T("Free Trial", "Deneme")}
                        </Link>
                        <button
                            type="button"
                            data-testid="nav-hamburger"
                            onClick={() => setDrawerOpen(true)}
                            aria-label="Open menu"
                            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-[#F5F0E8] hover:text-[#C9A84C]"
                        >
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer */}
            {drawerOpen && (
                <div className="fixed inset-0 z-[60] lg:hidden" data-testid="nav-mobile-drawer">
                    <div className="absolute inset-0 bg-[#0D1B3E]/60" onClick={() => setDrawerOpen(false)} />
                    <div className="absolute inset-y-0 right-0 w-[86%] max-w-sm bg-[#0D1B3E] text-[#F5F0E8] overflow-y-auto shadow-2xl">
                        <div className="flex items-center justify-between px-5 h-16 border-b border-[#F5F0E8]/10">
                            <div className="flex items-center gap-3">
                                <EuStars size={24} />
                                <span className="font-display text-lg">EuroLaw Hub</span>
                            </div>
                            <button type="button" data-testid="nav-drawer-close" onClick={() => setDrawerOpen(false)}
                                aria-label="Close menu"
                                className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:text-[#C9A84C]">
                                <X size={22} />
                            </button>
                        </div>
                        <div className="px-5 py-6">
                            <div className="mb-6">
                                <div className="label-eyebrow mb-2">{T("Product", "Ürün")}</div>
                                {PRODUCT_ITEMS.map((it) => (
                                    <button key={it.en} type="button" onClick={() => go(it.to)}
                                        className="block w-full text-left text-[15px] text-[#F5F0E8]/85 hover:text-[#C9A84C] min-h-[44px] flex items-center">
                                        {T(it.en, it.tr)}
                                    </button>
                                ))}
                            </div>
                            <div className="border-t border-[#F5F0E8]/10 pt-5 mb-6">
                                {LINKS.map((l) => (
                                    <button key={l.en} type="button" onClick={() => go(l.to)}
                                        className="block w-full text-left text-[15px] text-[#F5F0E8]/85 hover:text-[#C9A84C] min-h-[44px] flex items-center">
                                        {T(l.en, l.tr)}
                                    </button>
                                ))}
                                <button type="button" onClick={() => go("/login")} data-testid="nav-drawer-login"
                                    className="block w-full text-left text-[15px] text-[#F5F0E8]/85 hover:text-[#C9A84C] min-h-[44px] flex items-center">
                                    {T("Sign in", "Giriş yap")}
                                </button>
                                <div className="mt-2"><LangSwitcher dark testId="drawer-lang-switcher" /></div>
                            </div>
                            <button type="button" onClick={() => go("/signup")} data-testid="nav-drawer-signup"
                                className="btn-primary w-full min-h-[48px]">
                                {T("Start Free Trial", "Ücretsiz Deneme")} <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
